import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Truck, Package, CheckCircle, MapPin, Phone, Camera,
  AlertTriangle, LogOut, RefreshCw, Store, Play, X,
  Home, Eye, EyeOff, ChevronRight, Wifi, WifiOff,
  Moon, Sun, FileText, PenSquare, GripVertical, ArrowLeft,
  Map, Navigation, Minus, Plus, RotateCcw
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  requestNotificationPermission, notifyNewOrder, playNotificationSound
} from '../utils/notifications';
import { API_URL, getWsUrl } from '../config';

// ==================== Estilos (preserva identidad visual) ====================
const styles = `
@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
@keyframes bounceIn { 0% { transform: scale(0.9); } 50% { transform: scale(1.05); } 100% { transform: scale(1); } }

.bottom-sheet-enter { animation: slideUp 0.3s cubic-bezier(0.32, 0.72, 0, 1); }
.view-fade { animation: fadeIn 0.2s ease-out; }
.scale-in { animation: scaleIn 0.2s ease-out; }
.bounce-in { animation: bounceIn 0.3s ease-out; }
.active-press { transition: transform 0.1s; }
.active-press:active { transform: scale(0.97); }

.dark-mode { background-color: #0f172a !important; color: #f1f5f9; }
.dark-mode .bg-gray-50, .dark-mode .bg-gray-100 { background-color: #0f172a !important; }
.dark-mode .bg-white { background-color: #1e293b !important; }
.dark-mode .text-gray-900 { color: #f1f5f9 !important; }
.dark-mode .text-gray-800 { color: #e2e8f0 !important; }
.dark-mode .text-gray-700 { color: #cbd5e1 !important; }
.dark-mode .text-gray-600 { color: #94a3b8 !important; }
.dark-mode .text-gray-500 { color: #94a3b8 !important; }
.dark-mode .text-gray-400 { color: #64748b !important; }
.dark-mode .border-gray-100, .dark-mode .border-gray-200 { border-color: #334155 !important; }
.dark-mode .border-gray-300 { border-color: #475569 !important; }
.dark-mode .divide-gray-100 > :not(:first-child) { border-color: #334155 !important; }

.dark-mode input, .dark-mode textarea, .dark-mode select {
  background-color: #0f172a !important; color: #f1f5f9 !important; border-color: #334155 !important;
}
.dark-mode input::placeholder, .dark-mode textarea::placeholder { color: #64748b !important; }
.dark-mode .shadow-sm { box-shadow: none !important; }
.dark-mode .shadow-lg { box-shadow: 0 4px 24px rgba(0,0,0,0.4) !important; }

/* --- Fondos tonales: más oscuros y con tinte del color --- */
.dark-mode .bg-blue-50 { background-color: rgba(59,130,246,0.12) !important; }
.dark-mode .bg-blue-100 { background-color: rgba(59,130,246,0.22) !important; }
.dark-mode .bg-indigo-50 { background-color: rgba(99,102,241,0.12) !important; }
.dark-mode .bg-indigo-100 { background-color: rgba(99,102,241,0.22) !important; }
.dark-mode .bg-green-50 { background-color: rgba(34,197,94,0.12) !important; }
.dark-mode .bg-green-100 { background-color: rgba(34,197,94,0.22) !important; }
.dark-mode .bg-amber-50 { background-color: rgba(245,158,11,0.12) !important; }
.dark-mode .bg-amber-100 { background-color: rgba(245,158,11,0.22) !important; }
.dark-mode .bg-red-50 { background-color: rgba(239,68,68,0.15) !important; }
.dark-mode .bg-red-100 { background-color: rgba(239,68,68,0.25) !important; }
.dark-mode .bg-purple-50 { background-color: rgba(168,85,247,0.12) !important; }
.dark-mode .bg-purple-100 { background-color: rgba(168,85,247,0.22) !important; }

/* Gradientes Tailwind usan --tw-gradient-from / --tw-gradient-to */
.dark-mode .from-blue-50 { --tw-gradient-from: rgba(59,130,246,0.18) !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(59,130,246,0)) !important; }
.dark-mode .from-blue-100 { --tw-gradient-from: rgba(59,130,246,0.25) !important; }
.dark-mode .to-indigo-50 { --tw-gradient-to: rgba(99,102,241,0.18) !important; }
.dark-mode .to-indigo-100 { --tw-gradient-to: rgba(99,102,241,0.25) !important; }

/* Bordes tonales */
.dark-mode .border-blue-100 { border-color: rgba(59,130,246,0.35) !important; }
.dark-mode .border-blue-200 { border-color: rgba(59,130,246,0.45) !important; }
.dark-mode .border-indigo-100 { border-color: rgba(99,102,241,0.35) !important; }
.dark-mode .border-indigo-200 { border-color: rgba(99,102,241,0.45) !important; }
.dark-mode .border-amber-100 { border-color: rgba(245,158,11,0.35) !important; }
.dark-mode .border-amber-200 { border-color: rgba(245,158,11,0.45) !important; }
.dark-mode .border-green-100 { border-color: rgba(34,197,94,0.35) !important; }
.dark-mode .border-red-100 { border-color: rgba(239,68,68,0.35) !important; }

/* Textos tonales: suficiente contraste sobre fondos oscuros */
.dark-mode .text-blue-600 { color: #93c5fd !important; }
.dark-mode .text-blue-700 { color: #93c5fd !important; }
.dark-mode .text-blue-800 { color: #bfdbfe !important; }
.dark-mode .text-indigo-600 { color: #a5b4fc !important; }
.dark-mode .text-indigo-700 { color: #a5b4fc !important; }
.dark-mode .text-green-600 { color: #86efac !important; }
.dark-mode .text-green-700 { color: #86efac !important; }
.dark-mode .text-amber-700 { color: #fbbf24 !important; }
.dark-mode .text-amber-800 { color: #fcd34d !important; }
.dark-mode .text-amber-900 { color: #fde68a !important; }
.dark-mode .text-red-600 { color: #fca5a5 !important; }
.dark-mode .text-red-700 { color: #fca5a5 !important; }
.dark-mode .text-purple-600 { color: #d8b4fe !important; }
.dark-mode .text-purple-700 { color: #d8b4fe !important; }

/* bg-white/60 (la tarjeta de notas dentro del AddressBlock) queda semitransparente
   sobre el fondo oscuro — fijamos un tono legible */
.dark-mode .bg-white\\/60 { background-color: rgba(30,41,59,0.7) !important; }

/* DnD */
.dnd-item-dragging { opacity: 0.6; box-shadow: 0 12px 30px rgba(0,0,0,0.22); }
`;

// ==================== Constantes ====================
const SS_KEY = 'business_driver_session_token';
const DRIVER_KEY = 'business_driver_profile';
const DARK_KEY = 'business_delivery_dark_mode';
const ONBOARD_KEY = 'business_delivery_onboarding_done';

const STATUS_LABELS = {
  pending: 'Pendiente', assigned: 'Por recoger', picked_up: 'Recogida',
  in_transit: 'En camino', delivered: 'Entregada', failed: 'Fallida', cancelled: 'Cancelada',
};

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  assigned: 'bg-blue-100 text-blue-700',
  picked_up: 'bg-indigo-100 text-indigo-700',
  in_transit: 'bg-purple-100 text-purple-700',
  delivered: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-200 text-gray-600',
};

// ==================== Utilidades ====================
function urlB64ToUint8Array(base64) {
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const base = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base);
  return new Uint8Array([...raw].map(c => c.charCodeAt(0)));
}

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

const buildMapsUrl = (address) => {
  // Deep link universal: en Android/iOS abre la app de mapas instalada
  const q = encodeURIComponent(address || '');
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
};
const buildWazeUrl = (address) => {
  const q = encodeURIComponent(address || '');
  return `https://waze.com/ul?q=${q}&navigate=yes`;
};

// ==================== SignaturePad (táctil) ====================
const SignaturePad = ({ onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const start = (e) => { e.preventDefault(); drawingRef.current = true; lastRef.current = getPos(e); };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
  };
  const end = () => { drawingRef.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };
  const save = () => onSave(canvasRef.current.toDataURL('image/png'));

  return (
    <div className="bg-white p-4 rounded-xl scale-in">
      <p className="text-sm text-gray-600 mb-2">Firma del receptor</p>
      <canvas
        ref={canvasRef} width={600} height={220}
        className="w-full border-2 border-gray-300 rounded-lg touch-none bg-white"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div className="flex gap-2 mt-3">
        <button onClick={clear} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg active-press">Limpiar</button>
        <button onClick={onCancel} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg active-press">Cancelar</button>
        <button onClick={save} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg active-press">Guardar</button>
      </div>
    </div>
  );
};

// ==================== Subcomponentes ====================
const EmptyState = ({ icon: Icon, text, subtext }) => (
  <div className="bg-white rounded-xl p-10 text-center scale-in">
    <Icon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
    <p className="text-gray-700 font-medium text-sm">{text}</p>
    {subtext && <p className="text-gray-500 text-xs mt-1">{subtext}</p>}
  </div>
);

const AddressBlock = ({ address, notes }) => {
  const hasAddress = !!(address && address.trim());
  return (
    <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0 border border-blue-100">
          <MapPin className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wide text-blue-700 font-semibold">Entregar en</div>
          <div className="text-base font-semibold text-gray-900 mt-0.5 break-words">
            {hasAddress ? address : <span className="text-gray-400">Sin dirección capturada</span>}
          </div>
          {notes && (
            <div className="text-xs text-gray-600 mt-1 bg-white/60 rounded-lg p-2 border border-blue-100">
              <span className="font-medium text-gray-700">Nota de entrega:</span> {notes}
            </div>
          )}
        </div>
      </div>
      {hasAddress && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <a href={buildMapsUrl(address)} target="_blank" rel="noreferrer"
            className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white text-blue-700 text-sm font-medium border border-blue-200 active-press">
            <Map className="w-4 h-4" /> Google Maps
          </a>
          <a href={buildWazeUrl(address)} target="_blank" rel="noreferrer"
            className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white text-indigo-700 text-sm font-medium border border-indigo-200 active-press">
            <Navigation className="w-4 h-4" /> Waze
          </a>
        </div>
      )}
    </div>
  );
};

const DeliveryCard = ({ delivery, onClick, right, className = '' }) => {
  const statusCls = STATUS_COLORS[delivery.status] || 'bg-gray-100 text-gray-700';
  return (
    <div onClick={onClick} className={`bg-white rounded-xl p-4 shadow-sm ${onClick ? 'cursor-pointer active-press' : ''} ${className}`}>
      <div className="flex items-start justify-between mb-2 gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600 shrink-0" />
            <span className="truncate">{delivery.folio || 'Sin folio'}</span>
          </div>
          <div className="text-xs text-gray-500 mt-0.5 truncate">{delivery.customer_name || 'Cliente'}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusCls} whitespace-nowrap`}>
            {STATUS_LABELS[delivery.status] || delivery.status}
          </span>
          {right}
        </div>
      </div>
      {delivery.delivery_address && (
        <div className="text-sm text-gray-600 mt-2 flex items-start gap-2">
          <MapPin className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
          <span className="break-words">{delivery.delivery_address}</span>
        </div>
      )}
      <div className="mt-3 border-t border-gray-100 pt-2 flex items-center justify-between text-xs text-gray-500">
        <span>{(delivery.items || []).length} artículo(s)</span>
        <span className="font-semibold text-gray-700">{fmtMoney(delivery.total)}</span>
      </div>
    </div>
  );
};

const DetailSheet = ({ delivery, onClose, onAccept, onDeliver, onProblem, onPickup, onInTransit, onRelease, loading }) => {
  if (!delivery) return null;
  const canAccept = delivery.status === 'pending';
  const canPickup = delivery.status === 'assigned';
  const canTransit = delivery.status === 'picked_up';
  const canDeliver = ['assigned', 'picked_up', 'in_transit'].includes(delivery.status);
  const canRelease = delivery.status === 'assigned';

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto bottom-sheet-enter">
        <div className="sticky top-0 bg-white z-10 px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="p-2 -ml-2 rounded-lg hover:bg-gray-100">
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div>
              <div className="font-bold text-gray-900">{delivery.folio}</div>
              <div className="text-xs text-gray-500">{delivery.customer_name}</div>
            </div>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS[delivery.status]}`}>
            {STATUS_LABELS[delivery.status]}
          </span>
        </div>

        <div className="p-4 space-y-4">
          <AddressBlock address={delivery.delivery_address} notes={delivery.delivery_notes} />

          {delivery.customer_phone && (
            <a href={`tel:${delivery.customer_phone}`}
              className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 p-3 active-press">
              <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                <Phone className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-gray-500">Llamar al cliente</div>
                <div className="font-semibold text-gray-900">{delivery.customer_phone}</div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </a>
          )}

          {/* Items */}
          <div className="bg-white rounded-xl border border-gray-100">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <div className="font-semibold text-gray-900">Productos a entregar</div>
              <div className="text-xs text-gray-500">{(delivery.items || []).length} ítem(s)</div>
            </div>
            <div className="divide-y divide-gray-100">
              {(delivery.items || []).map((it, idx) => (
                <div key={it.id || idx} className="px-4 py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 break-words">{it.product_name}</div>
                    {it.product_sku && <div className="text-[11px] text-gray-500 mt-0.5">SKU: {it.product_sku}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-gray-900">{Number(it.quantity).toLocaleString('es-MX')}</div>
                    <div className="text-[11px] text-gray-500">{fmtMoney(it.unit_price)} c/u</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50 rounded-b-xl">
              <div className="text-sm text-gray-600">Total</div>
              <div className="text-lg font-bold text-gray-900">{fmtMoney(delivery.total)}</div>
            </div>
          </div>

          {delivery.internal_notes && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
              <div className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold">Notas internas</div>
              <div className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{delivery.internal_notes}</div>
            </div>
          )}
        </div>

        {/* Acciones pegadas abajo */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-4 py-3 space-y-2">
          {canAccept && onAccept && (
            <button onClick={() => onAccept(delivery)} disabled={loading}
              className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg active-press disabled:opacity-50">
              Tomar esta remisión
            </button>
          )}
          {canPickup && onPickup && (
            <button onClick={() => onPickup(delivery)} disabled={loading}
              className="w-full py-3 bg-indigo-600 text-white font-semibold rounded-lg active-press disabled:opacity-50">
              <Package className="inline w-4 h-4 mr-2" /> Confirmar que recogí
            </button>
          )}
          {canTransit && onInTransit && (
            <button onClick={() => onInTransit(delivery)} disabled={loading}
              className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg active-press disabled:opacity-50">
              <Play className="inline w-4 h-4 mr-2" /> En camino
            </button>
          )}
          {canDeliver && onDeliver && (
            <button onClick={() => onDeliver(delivery)}
              className="w-full py-3 bg-green-600 text-white font-semibold rounded-lg active-press">
              <CheckCircle className="inline w-4 h-4 mr-2" /> Iniciar entrega
            </button>
          )}
          {canDeliver && onProblem && (
            <button onClick={() => onProblem(delivery)}
              className="w-full py-2 bg-red-50 text-red-700 font-medium rounded-lg active-press">
              <AlertTriangle className="inline w-4 h-4 mr-2" /> Reportar problema
            </button>
          )}
          {canRelease && onRelease && (
            <button onClick={() => onRelease(delivery)} disabled={loading}
              className="w-full py-2 text-gray-600 text-sm font-medium rounded-lg active-press">
              <RotateCcw className="inline w-3 h-3 mr-2" /> Soltar (regresar a pendientes)
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== Sortable card ====================
const SortableDeliveryCard = ({ delivery, index, onOpen }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: delivery.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'dnd-item-dragging' : ''}>
      <div className="bg-white rounded-xl shadow-sm flex items-stretch overflow-hidden">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="px-2 flex flex-col items-center justify-center bg-gray-50 border-r border-gray-100 text-gray-400 touch-none"
          aria-label="Arrastrar para reordenar"
        >
          <GripVertical className="w-5 h-5" />
          <span className="text-[10px] font-bold mt-1 text-gray-500">#{index + 1}</span>
        </button>
        <div className="flex-1 p-3 active-press cursor-pointer" onClick={() => onOpen(delivery)}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="min-w-0">
              <div className="font-semibold text-gray-900 text-sm flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-blue-600" />
                <span className="truncate">{delivery.folio}</span>
              </div>
              <div className="text-xs text-gray-500 truncate">{delivery.customer_name}</div>
            </div>
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[delivery.status]} shrink-0`}>
              {STATUS_LABELS[delivery.status]}
            </span>
          </div>
          {delivery.delivery_address && (
            <div className="text-xs text-gray-600 flex items-start gap-1 mt-1.5">
              <MapPin className="w-3 h-3 text-gray-400 mt-0.5 shrink-0" />
              <span className="break-words line-clamp-2">{delivery.delivery_address}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-[11px] text-gray-500 mt-1.5 pt-1.5 border-t border-gray-100">
            <span>{(delivery.items || []).length} art.</span>
            <span className="font-semibold text-gray-700">{fmtMoney(delivery.total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== Formulario de entrega con cantidades ====================
const DeliverForm = ({ delivery, onSubmit, onCancel, loading }) => {
  const [receivedBy, setReceivedBy] = useState('');
  const [receivedIdDoc, setReceivedIdDoc] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [proofPhoto, setProofPhoto] = useState(null);
  const [signature, setSignature] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  // Cantidades entregadas por item (default = quantity)
  const [qtyByItem, setQtyByItem] = useState(() => {
    const o = {};
    (delivery.items || []).forEach(it => { o[it.id] = String(it.quantity); });
    return o;
  });

  const onPhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setProofPhoto(ev.target.result);
    reader.readAsDataURL(file);
  };

  const setQty = (id, val) => {
    // Permitir cadena vacía mientras tipea, validar al submit
    setQtyByItem(prev => ({ ...prev, [id]: val }));
  };

  const stepQty = (id, delta, maxQty) => {
    setQtyByItem(prev => {
      const cur = parseFloat(prev[id] || '0') || 0;
      let next = cur + delta;
      if (next < 0) next = 0;
      if (next > maxQty) next = maxQty;
      // Redondeo para evitar 0.9999999
      next = Math.round(next * 1000) / 1000;
      return { ...prev, [id]: String(next) };
    });
  };

  const handleSubmit = () => {
    if (!receivedBy.trim()) {
      toast.error('Indica quién recibió');
      return;
    }
    const items = (delivery.items || []).map(it => {
      const raw = qtyByItem[it.id];
      const n = parseFloat(raw);
      const clean = Number.isFinite(n) && n >= 0 ? n : Number(it.quantity || 0);
      return { id: it.id, quantity_delivered: clean };
    });
    onSubmit({
      received_by: receivedBy,
      received_id_doc: receivedIdDoc || null,
      notes: deliveryNotes || null,
      proof_photo: proofPhoto || null,
      signature: signature || null,
      items,
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto bottom-sheet-enter">
        <div className="sticky top-0 bg-white z-10 px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={onCancel} className="p-2 -ml-2 rounded-lg hover:bg-gray-100">
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div>
              <div className="font-bold text-gray-900">Confirmar entrega</div>
              <div className="text-xs text-gray-500">{delivery.folio} · {delivery.customer_name}</div>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Recibió *</label>
            <input value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200"
              placeholder="Nombre del receptor" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Identificación (opcional)</label>
            <input value={receivedIdDoc} onChange={(e) => setReceivedIdDoc(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200"
              placeholder="INE / cédula" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas de entrega (opcional)</label>
            <textarea value={deliveryNotes} onChange={(e) => setDeliveryNotes(e.target.value)} rows={2}
              className="w-full px-3 py-2 rounded-lg border border-gray-200"
              placeholder="Observaciones" />
          </div>

          {/* Cantidades por producto */}
          <div className="bg-white rounded-xl border border-gray-100">
            <div className="px-4 py-3 border-b border-gray-100">
              <div className="font-semibold text-gray-900">Cantidades entregadas</div>
              <div className="text-xs text-gray-500 mt-0.5">
                Por default se entrega todo. Ajusta solo si hay devolución parcial.
              </div>
            </div>
            <div className="divide-y divide-gray-100">
              {(delivery.items || []).map(it => {
                const maxQty = Number(it.quantity || 0);
                const curVal = qtyByItem[it.id] ?? '0';
                const curNum = parseFloat(curVal) || 0;
                const diff = maxQty - curNum;
                return (
                  <div key={it.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 break-words">{it.product_name}</div>
                        {it.product_sku && <div className="text-[11px] text-gray-500 mt-0.5">SKU: {it.product_sku}</div>}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[11px] text-gray-500">Lleva</div>
                        <div className="text-sm font-semibold text-gray-700">
                          {Number(it.quantity).toLocaleString('es-MX')}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => stepQty(it.id, -1, maxQty)}
                        className="w-9 h-9 rounded-lg bg-gray-100 text-gray-700 flex items-center justify-center active-press">
                        <Minus className="w-4 h-4" />
                      </button>
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        max={maxQty}
                        value={curVal}
                        onChange={(e) => setQty(it.id, e.target.value)}
                        className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-center font-semibold"
                      />
                      <button type="button" onClick={() => stepQty(it.id, 1, maxQty)}
                        className="w-9 h-9 rounded-lg bg-gray-100 text-gray-700 flex items-center justify-center active-press">
                        <Plus className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => setQty(it.id, String(maxQty))}
                        className="px-3 py-2 rounded-lg text-xs bg-blue-50 text-blue-700 font-medium active-press">
                        Todo
                      </button>
                    </div>
                    {diff > 0 && (
                      <div className="mt-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2 py-1">
                        Se devolverá: {diff.toLocaleString('es-MX', { maximumFractionDigits: 3 })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Foto */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Foto (opcional)</label>
            <label className="block cursor-pointer">
              <input type="file" accept="image/*" capture="environment" onChange={onPhotoCapture} className="hidden" />
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center bg-white">
                {proofPhoto ? (
                  <img src={proofPhoto} alt="proof" className="w-full max-h-40 object-contain rounded" />
                ) : (
                  <div className="text-sm text-gray-500 flex items-center justify-center gap-2">
                    <Camera className="w-5 h-5" /> Tomar foto
                  </div>
                )}
              </div>
            </label>
          </div>

          {/* Firma */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Firma (opcional)</label>
            {signature ? (
              <div className="relative">
                <img src={signature} alt="firma" className="w-full border border-gray-200 rounded-lg bg-white" />
                <button onClick={() => setSignature(null)} className="absolute top-2 right-2 p-1 bg-white rounded-full shadow">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : showSignaturePad ? (
              <SignaturePad onSave={(d) => { setSignature(d); setShowSignaturePad(false); }} onCancel={() => setShowSignaturePad(false)} />
            ) : (
              <button onClick={() => setShowSignaturePad(true)} className="w-full py-3 bg-gray-100 rounded-lg text-sm flex items-center justify-center gap-2">
                <PenSquare className="w-4 h-4" /> Capturar firma
              </button>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-4 py-3">
          <button onClick={handleSubmit} disabled={loading}
            className="w-full py-3 bg-green-600 text-white font-semibold rounded-lg active-press disabled:opacity-50">
            {loading ? 'Guardando...' : 'Confirmar entrega'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== Formulario problema ====================
const ProblemForm = ({ delivery, onSubmit, onCancel, loading }) => {
  const [problemType, setProblemType] = useState('customer_absent');
  const [problemNotes, setProblemNotes] = useState('');
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl bottom-sheet-enter">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={onCancel} className="p-2 -ml-2 rounded-lg hover:bg-gray-100">
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div>
              <div className="font-bold text-gray-900">Reportar problema</div>
              <div className="text-xs text-gray-500">{delivery.folio}</div>
            </div>
          </div>
        </div>
        <div className="p-4 space-y-3">
          <select value={problemType} onChange={(e) => setProblemType(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-200">
            <option value="customer_absent">Cliente ausente</option>
            <option value="wrong_address">Dirección incorrecta</option>
            <option value="product_damaged">Producto dañado</option>
            <option value="customer_rejected">Cliente rechazó</option>
            <option value="access_denied">No se pudo acceder</option>
            <option value="other">Otro</option>
          </select>
          <textarea value={problemNotes} onChange={(e) => setProblemNotes(e.target.value)} rows={3}
            className="w-full px-3 py-2 rounded-lg border border-gray-200" placeholder="Describe el problema" />
          <button onClick={() => onSubmit({ problem_type: problemType, problem_notes: problemNotes })} disabled={loading}
            className="w-full py-3 bg-red-600 text-white font-semibold rounded-lg active-press disabled:opacity-50">
            {loading ? 'Guardando...' : 'Reportar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== App principal ====================
const DeliveryBusinessApp = () => {
  // Sesión
  const [sessionToken, setSessionToken] = useState(localStorage.getItem(SS_KEY) || '');
  const [driver, setDriver] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DRIVER_KEY) || 'null'); } catch { return null; }
  });
  const [branches, setBranches] = useState([]);
  const [needsBranchSelection, setNeedsBranchSelection] = useState(false);
  const [, setSelectedBranchId] = useState(null);

  // UI
  const [tab, setTab] = useState('pending');
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem(DARK_KEY) === '1');
  const [showOnboard, setShowOnboard] = useState(false);
  const [showPwaPrompt, setShowPwaPrompt] = useState(false);
  const deferredPromptRef = useRef(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Datos
  const [pendingList, setPendingList] = useState([]);
  const [myList, setMyList] = useState([]);
  const [historyList, setHistoryList] = useState([]);

  // Multi-select
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Overlays
  const [detailDelivery, setDetailDelivery] = useState(null);
  const [deliverDelivery, setDeliverDelivery] = useState(null);
  const [problemDelivery, setProblemDelivery] = useState(null);

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // WS
  const wsRef = useRef(null);
  const wsReconnectRef = useRef(null);

  // GPS (mientras haya entregas activas)
  const locationWatchRef = useRef(null);

  const isAuthenticated = !!(sessionToken && driver && !needsBranchSelection);

  // ==================== Sensores DnD ====================
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } })
  );

  // ==================== Dark mode, online ====================
  useEffect(() => { document.body.classList.toggle('dark-mode', darkMode); }, [darkMode]);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // ==================== PWA install prompt ====================
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      if (!localStorage.getItem('business_delivery_pwa_dismissed')) setShowPwaPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);
  const installPwa = async () => {
    const dp = deferredPromptRef.current; if (!dp) return;
    dp.prompt(); await dp.userChoice;
    deferredPromptRef.current = null; setShowPwaPrompt(false);
  };
  const dismissPwa = () => {
    localStorage.setItem('business_delivery_pwa_dismissed', '1'); setShowPwaPrompt(false);
  };

  // ==================== Push ====================
  const subscribePush = useCallback(async (token) => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const vapidRes = await fetch(`${API_URL}/api/v1/remissions-delivery/push/vapid-key`);
      const { public_key } = await vapidRes.json();
      if (!public_key) return;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(public_key),
      });
      await fetch(`${API_URL}/api/v1/remissions-delivery/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: token, subscription: sub }),
      });
    } catch (err) { console.warn('Push falló:', err?.message); }
  }, []);

  // ==================== Auth ====================
  const handleLogout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/v1/remissions-delivery/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: sessionToken }),
      });
    } catch {}
    localStorage.removeItem(SS_KEY);
    localStorage.removeItem(DRIVER_KEY);
    setSessionToken(''); setDriver(null); setSelectedBranchId(null); setBranches([]);
    setPendingList([]); setMyList([]); setHistoryList([]); setSelectedIds(new Set());
  }, [sessionToken]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/remissions-delivery/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error de autenticación');
      localStorage.setItem(SS_KEY, data.driver.session_token);
      localStorage.setItem(DRIVER_KEY, JSON.stringify(data.driver));
      setSessionToken(data.driver.session_token);
      setDriver(data.driver);
      setBranches(data.branches || []);
      setNeedsBranchSelection(!!data.requires_branch_selection);
      if (!data.requires_branch_selection && data.auto_selected_branch) {
        setSelectedBranchId(data.auto_selected_branch.id);
      }
      toast.success('Bienvenido');
      await subscribePush(data.driver.session_token);
      if (!localStorage.getItem(ONBOARD_KEY)) setShowOnboard(true);
      requestNotificationPermission();
    } catch (err) {
      setLoginError(err.message);
    } finally { setLoading(false); }
  };

  const handleSelectBranch = async (branchId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/remissions-delivery/select-branch`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: sessionToken, branch_id: branchId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      setSelectedBranchId(branchId);
      setNeedsBranchSelection(false);
      const updated = { ...driver, current_branch_id: branchId };
      localStorage.setItem(DRIVER_KEY, JSON.stringify(updated));
      setDriver(updated);
      toast.success(`Sucursal ${data.branch?.name || ''} seleccionada`);
    } catch (err) {
      toast.error(err.message);
    } finally { setLoading(false); }
  };

  // ==================== Fetch ====================
  const fetchPending = useCallback(async () => {
    if (!sessionToken) return;
    try {
      const res = await fetch(`${API_URL}/api/v1/remissions-delivery/pending?session_token=${sessionToken}`);
      if (res.status === 401) { handleLogout(); return; }
      const data = await res.json();
      if (Array.isArray(data)) setPendingList(data);
    } catch (err) { console.warn('fetchPending:', err?.message); }
  }, [sessionToken, handleLogout]);

  const fetchMine = useCallback(async () => {
    if (!sessionToken) return;
    try {
      const res = await fetch(`${API_URL}/api/v1/remissions-delivery/my-deliveries?session_token=${sessionToken}`);
      if (res.status === 401) { handleLogout(); return; }
      const data = await res.json();
      if (Array.isArray(data)) setMyList(data);
    } catch (err) { console.warn('fetchMine:', err?.message); }
  }, [sessionToken, handleLogout]);

  const fetchHistory = useCallback(async () => {
    if (!sessionToken) return;
    try {
      const res = await fetch(`${API_URL}/api/v1/remissions-delivery/history?session_token=${sessionToken}&limit=50`);
      const data = await res.json();
      if (Array.isArray(data)) setHistoryList(data);
    } catch (err) { console.warn('fetchHistory:', err?.message); }
  }, [sessionToken]);

  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([fetchPending(), fetchMine(), fetchHistory()]);
    setRefreshing(false);
  }, [fetchPending, fetchMine, fetchHistory]);

  useEffect(() => {
    if (!isAuthenticated) return;
    refreshAll();
    const iv = setInterval(refreshAll, 30000);
    return () => clearInterval(iv);
  }, [isAuthenticated, refreshAll]);

  // ==================== WebSocket ====================
  useEffect(() => {
    if (!isAuthenticated || !driver?.id || !sessionToken) return;
    const connect = () => {
      try {
        const url = `${getWsUrl()}/api/v1/remissions-delivery/ws/driver/${driver.id}?token=${sessionToken}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onmessage = (evt) => {
          try {
            if (evt.data === 'pong') return;
            const msg = JSON.parse(evt.data);
            if (msg.type === 'remission:new') {
              try { playNotificationSound?.(); } catch {}
              try { notifyNewOrder?.('Nueva remisión disponible', `${msg.delivery?.folio || ''}`); } catch {}
              toast.info(`Nueva remisión: ${msg.delivery?.folio || ''}`);
              fetchPending();
            } else if (msg.type === 'remission:update') {
              fetchPending(); fetchMine();
            }
          } catch {}
        };
        ws.onclose = () => {
          wsRef.current = null;
          wsReconnectRef.current = setTimeout(connect, 3000);
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
        const pingIv = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 30000);
        ws._pingIv = pingIv;
      } catch {
        wsReconnectRef.current = setTimeout(connect, 3000);
      }
    };
    connect();
    return () => {
      if (wsReconnectRef.current) clearTimeout(wsReconnectRef.current);
      if (wsRef.current) {
        try { clearInterval(wsRef.current._pingIv); } catch {}
        try { wsRef.current.close(); } catch {}
      }
    };
  }, [isAuthenticated, driver?.id, sessionToken, fetchPending, fetchMine]);

  // ==================== GPS ====================
  useEffect(() => {
    const hasActive = myList.some(d => ['assigned', 'picked_up', 'in_transit'].includes(d.status));
    if (!isAuthenticated || !hasActive || !navigator.geolocation) return;
    locationWatchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          try { wsRef.current.send(JSON.stringify({ type: 'location_update', lat, lng })); } catch {}
        } else {
          fetch(`${API_URL}/api/v1/remissions-delivery/location`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_token: sessionToken, lat, lng }),
          }).catch(() => {});
        }
      },
      (err) => console.warn('GPS:', err.message),
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 30000 }
    );
    return () => {
      if (locationWatchRef.current != null) {
        try { navigator.geolocation.clearWatch(locationWatchRef.current); } catch {}
        locationWatchRef.current = null;
      }
    };
  }, [isAuthenticated, myList, sessionToken]);

  // ==================== Multi-select ====================
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const selectedPending = useMemo(
    () => pendingList.filter(d => selectedIds.has(d.id)),
    [pendingList, selectedIds]
  );

  const handleAcceptBulk = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/remissions-delivery/accept-bulk`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: sessionToken, ids }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      toast.success(`Tomadas ${data.accepted_count} remisión(es)${data.skipped_count ? `, ${data.skipped_count} ya no disponibles` : ''}`);
      clearSelection();
      await refreshAll();
      setTab('mine');
    } catch (err) {
      toast.error(err.message);
    } finally { setLoading(false); }
  };

  // ==================== Transiciones ====================
  const handleAcceptOne = async (d) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/remissions-delivery/${d.id}/accept`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: sessionToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      toast.success('Remisión aceptada');
      setDetailDelivery(null);
      await refreshAll();
      setTab('mine');
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };

  const handleTransition = async (d, action) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/remissions-delivery/${d.id}/${action}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: sessionToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      toast.success('Estado actualizado');
      setDetailDelivery(data.delivery);
      await refreshAll();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };

  const handleRelease = async (d) => {
    if (!window.confirm('¿Soltar esta remisión? Regresará a la lista de pendientes.')) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/remissions-delivery/${d.id}/release`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: sessionToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      toast.success('Remisión liberada');
      setDetailDelivery(null);
      await refreshAll();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };

  const handleDeliverSubmit = async (payload) => {
    if (!deliverDelivery) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/remissions-delivery/${deliverDelivery.id}/deliver`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: sessionToken, ...payload }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      toast.success(data.caja_notified ? 'Entregada y notificada a Caja' : 'Entregada (Caja offline; se notificará al reconectar)');
      setDeliverDelivery(null);
      setDetailDelivery(null);
      await refreshAll();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };

  const handleProblemSubmit = async (payload) => {
    if (!problemDelivery) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/remissions-delivery/${problemDelivery.id}/problem`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: sessionToken, ...payload }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      toast.success('Problema reportado');
      setProblemDelivery(null);
      setDetailDelivery(null);
      await refreshAll();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };

  // ==================== Reorder (drag & drop) ====================
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = myList.findIndex(d => d.id === active.id);
    const newIndex = myList.findIndex(d => d.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(myList, oldIndex, newIndex);
    setMyList(reordered); // optimista

    try {
      const res = await fetch(`${API_URL}/api/v1/remissions-delivery/reorder`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_token: sessionToken,
          ids_in_order: reordered.map(d => d.id),
        }),
      });
      if (!res.ok) throw new Error('Error');
    } catch {
      toast.error('No se pudo guardar el orden');
      fetchMine();
    }
  };

  // ==================== Render: Login ====================
  if (!sessionToken || !driver) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${darkMode ? 'dark-mode' : 'bg-gradient-to-br from-blue-50 to-indigo-100'}`}>
        <style>{styles}</style>
        <Toaster position="top-center" richColors />
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-600 rounded-2xl mb-4">
              <Truck className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Entregas del Negocio</h1>
            <p className="text-sm text-gray-600 mt-1">Remisiones listas para entrega</p>
          </div>
          <form onSubmit={handleLogin} className="bg-white rounded-2xl shadow-sm p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Correo</label>
              <input type="email" required value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                placeholder="chofer@empresa.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} required value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-10 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
                <button type="button" className="absolute right-3 top-3 text-gray-400" onClick={() => setShowPassword(p => !p)}>
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            {loginError && <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{loginError}</div>}
            <button type="submit" disabled={loading}
              className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg active-press disabled:opacity-50">
              {loading ? 'Ingresando...' : 'Ingresar'}
            </button>
          </form>
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
            <button onClick={() => { const v = !darkMode; setDarkMode(v); localStorage.setItem(DARK_KEY, v ? '1' : '0'); }}>
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <span>•</span>
            <span>{online ? 'En línea' : 'Sin conexión'}</span>
          </div>
        </div>
      </div>
    );
  }

  if (needsBranchSelection) {
    return (
      <div className={`min-h-screen p-4 ${darkMode ? 'dark-mode' : 'bg-gray-50'}`}>
        <style>{styles}</style>
        <Toaster position="top-center" richColors />
        <div className="max-w-md mx-auto pt-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 text-center">Selecciona tu sucursal</h2>
          <div className="space-y-2">
            {branches.map(b => (
              <button key={b.id} onClick={() => handleSelectBranch(b.id)} disabled={loading}
                className="w-full bg-white p-4 rounded-xl shadow-sm flex items-center justify-between active-press">
                <div className="flex items-center gap-3">
                  <Store className="w-5 h-5 text-blue-600" />
                  <div className="text-left">
                    <div className="font-semibold text-gray-900">{b.name}</div>
                    {b.address && <div className="text-xs text-gray-500">{b.address?.street || ''}</div>}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            ))}
          </div>
          <button onClick={handleLogout} className="mt-6 w-full text-sm text-gray-500">Cerrar sesión</button>
        </div>
      </div>
    );
  }

  // ==================== Header + Tabs ====================
  const totalActive = myList.length;
  const deliveredCount = historyList.filter(d => d.status === 'delivered').length;

  return (
    <div className={`min-h-screen ${selectedIds.size > 0 ? 'pb-28' : 'pb-20'} ${darkMode ? 'dark-mode' : 'bg-gray-50'}`}>
      <style>{styles}</style>
      <Toaster position="top-center" richColors />

      <header className="sticky top-0 z-40 bg-white shadow-sm border-b border-gray-100">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">
              {driver.full_name?.[0]?.toUpperCase() || 'R'}
            </div>
            <div>
              <div className="font-semibold text-sm text-gray-900">{driver.full_name}</div>
              <div className="text-xs text-gray-500 flex items-center gap-1">
                {online ? <Wifi className="w-3 h-3 text-green-500" /> : <WifiOff className="w-3 h-3 text-red-500" />}
                {online ? 'En línea' : 'Sin conexión'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={refreshAll} className="p-2 rounded-lg hover:bg-gray-100" title="Refrescar">
              <RefreshCw className={`w-5 h-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={() => { const v = !darkMode; setDarkMode(v); localStorage.setItem(DARK_KEY, v ? '1' : '0'); }} className="p-2 rounded-lg hover:bg-gray-100">
              {darkMode ? <Sun className="w-5 h-5 text-gray-600" /> : <Moon className="w-5 h-5 text-gray-600" />}
            </button>
            <button onClick={handleLogout} className="p-2 rounded-lg hover:bg-gray-100" title="Cerrar sesión">
              <LogOut className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </header>

      {showPwaPrompt && (
        <div className="mx-4 mt-3 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between bounce-in">
          <div className="text-sm text-blue-800">Instala la app para acceso rápido</div>
          <div className="flex gap-2">
            <button onClick={dismissPwa} className="text-xs px-3 py-1 text-blue-800">No</button>
            <button onClick={installPwa} className="text-xs px-3 py-1 bg-blue-600 text-white rounded-lg">Instalar</button>
          </div>
        </div>
      )}

      <nav className="flex bg-white border-b border-gray-100 px-2">
        {[
          { key: 'pending', label: 'Pendientes', count: pendingList.length, icon: Package },
          { key: 'mine', label: 'Mi ruta', count: totalActive, icon: Truck },
          { key: 'history', label: 'Historial', count: deliveredCount, icon: FileText },
        ].map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); clearSelection(); }}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium border-b-2 ${tab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}>
            <t.icon className="w-4 h-4" />
            <span>{t.label}</span>
            {t.count > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${tab === t.key ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{t.count}</span>
            )}
          </button>
        ))}
      </nav>

      {/* ==================== Listas ==================== */}
      <main className="p-4 view-fade space-y-3">
        {tab === 'pending' && (
          pendingList.length === 0
            ? <EmptyState icon={Package} text="No hay remisiones pendientes" subtext="En cuanto bodega surta una aparecerá aquí" />
            : (
              <>
                <div className="text-xs text-gray-500 px-1">
                  {selectedIds.size === 0
                    ? 'Selecciona las que te asignaron para tomarlas de golpe'
                    : `${selectedIds.size} seleccionada(s)`}
                </div>
                {pendingList.map(d => (
                  <DeliveryCard key={d.id} delivery={d}
                    className={selectedIds.has(d.id) ? 'ring-2 ring-blue-500' : ''}
                    onClick={() => setDetailDelivery(d)}
                    right={
                      <label className="inline-flex items-center p-2 -mr-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(d.id)}
                          onChange={() => toggleSelect(d.id)}
                          className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500"
                        />
                      </label>
                    }
                  />
                ))}
              </>
            )
        )}

        {tab === 'mine' && (
          myList.length === 0
            ? <EmptyState icon={Truck} text="No tienes entregas en tu ruta" subtext="Toma remisiones de la pestaña Pendientes" />
            : (
              <>
                <div className="bg-white rounded-xl p-3 border border-gray-100 flex items-center gap-3">
                  <div className="flex-1">
                    <div className="text-xs text-gray-500">Ruta actual</div>
                    <div className="text-sm font-semibold text-gray-900">
                      {myList.length} entrega{myList.length === 1 ? '' : 's'} por realizar
                    </div>
                  </div>
                  <div className="text-[11px] text-gray-500">Arrastra <GripVertical className="inline w-3 h-3" /> para reordenar</div>
                </div>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={myList.map(d => d.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {myList.map((d, idx) => (
                        <SortableDeliveryCard key={d.id} delivery={d} index={idx}
                          onOpen={(x) => setDetailDelivery(x)} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </>
            )
        )}

        {tab === 'history' && (
          historyList.length === 0
            ? <EmptyState icon={FileText} text="Sin historial" />
            : historyList.map(d => (
              <DeliveryCard key={d.id} delivery={d}
                onClick={() => setDetailDelivery(d)} />
            ))
        )}
      </main>

      {/* ==================== Bottom bar: tomar seleccionadas ==================== */}
      {tab === 'pending' && selectedIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-100 p-3 shadow-lg">
          <div className="max-w-screen-sm mx-auto flex items-center gap-2">
            <button onClick={clearSelection} className="px-3 py-3 text-sm text-gray-600 rounded-lg bg-gray-100">
              Cancelar
            </button>
            <button onClick={handleAcceptBulk} disabled={loading}
              className="flex-1 py-3 bg-blue-600 text-white font-semibold rounded-lg active-press disabled:opacity-50">
              Tomar {selectedIds.size} remisión{selectedIds.size === 1 ? '' : 'es'}
            </button>
          </div>
          {selectedPending.length > 0 && (
            <div className="text-[11px] text-gray-500 mt-1 text-center">
              Total: {fmtMoney(selectedPending.reduce((sum, d) => sum + Number(d.total || 0), 0))} ·
              {' '}{selectedPending.reduce((sum, d) => sum + (d.items || []).length, 0)} artículos
            </div>
          )}
        </div>
      )}

      {/* ==================== Overlays ==================== */}
      {detailDelivery && (
        <DetailSheet
          delivery={detailDelivery}
          loading={loading}
          onClose={() => setDetailDelivery(null)}
          onAccept={detailDelivery.status === 'pending' ? handleAcceptOne : null}
          onPickup={(d) => handleTransition(d, 'pickup')}
          onInTransit={(d) => handleTransition(d, 'in-transit')}
          onDeliver={(d) => { setDeliverDelivery(d); }}
          onProblem={(d) => { setProblemDelivery(d); }}
          onRelease={handleRelease}
        />
      )}

      {deliverDelivery && (
        <DeliverForm
          delivery={deliverDelivery}
          loading={loading}
          onCancel={() => setDeliverDelivery(null)}
          onSubmit={handleDeliverSubmit}
        />
      )}

      {problemDelivery && (
        <ProblemForm
          delivery={problemDelivery}
          loading={loading}
          onCancel={() => setProblemDelivery(null)}
          onSubmit={handleProblemSubmit}
        />
      )}

      {showOnboard && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 bottom-sheet-enter">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto bg-blue-600 rounded-2xl flex items-center justify-center mb-3">
                <Truck className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">Así funciona</h3>
              <ol className="text-sm text-gray-600 text-left space-y-2 list-decimal pl-5 mb-4">
                <li>En <strong>Pendientes</strong>, selecciona las remisiones que te asignaron.</li>
                <li>Toca <strong>Tomar N remisiones</strong> para agregarlas a tu ruta.</li>
                <li>En <strong>Mi ruta</strong> ordénalas arrastrando con el dedo <Home className="inline w-3 h-3" />.</li>
                <li>Al llegar al destino, abre la entrega, confirma cantidades y firma.</li>
              </ol>
              <button onClick={() => { setShowOnboard(false); localStorage.setItem(ONBOARD_KEY, '1'); }}
                className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg">Entendido</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryBusinessApp;
